import type { PrefillAndIframeAttrsConfig } from "@calcom/embed-core"
import { action } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import { tracked } from "@glimmer/tracking"
import type { Group } from "@shared/models/group"
import type { User } from "@shared/models/user"
import groupUnlockScheduleDemoAction from "cc-frontend/lib/actions/group/unlock/GROUP_UNLOCK_SCHEDULE_DEMO"
import type Dispatcher from "cc-frontend/services/dispatcher"
import styles from "./district-admin.module.css"

export interface UnlockGroupFormDistrictAdminSignature {
  Args: {
    closeModal: () => void
    model: {
      user: User
      /** Provided by LockedGroup but not by LockedGroupGeneric. */
      group?: Group
    }
  }
}

export default class UnlockGroupFormDistrictAdmin extends Component<UnlockGroupFormDistrictAdminSignature> {
  readonly styles = styles

  @service declare dispatcher: Dispatcher

  @tracked
  isMeetingScheduled = false

  createdGroupId: string | null = null

  get calConfig(): PrefillAndIframeAttrsConfig {
    const user = this.args.model.user
    const group = this.args.model.group

    return {
      layout: "month_view",
      name: user.attributes.fullName,
      email: user.attributes.email,
      ATTENDEES_ROLE: user.attributes.title,
      ONBOARDING_GOALS: user.attributes.onboardingGoals.join(", "),
      USER_ADMIN_LINK: `https://www.commonplanner.com/admin/users/${user.id}`,
      ...(group
        ? {
            // This is actually the "District Name:" input, but we label it ATTENDEE_SCHOOL so that
            // we can use the same General Meeting Reminder workflows as the other event types.
            ATTENDEE_SCHOOL: group.attributes.title,
            GROUP_ADMIN_LINK: `https://www.commonplanner.com/admin/groups/${group.id}`,
          }
        : {}),
    }
  }

  @action
  onMeetingScheduled(event: CustomEvent) {
    this.isMeetingScheduled = true

    const booking = event.detail?.data?.booking
    const titleFromBooking: string | undefined = booking?.responses?.ATTENDEE_SCHOOL
    if (titleFromBooking === undefined) {
      // The event we're using is deprecated and might stop providing this info at some point.
      console.error("Could not extract title from booking")
    }

    // Scroll to top so they can see the button.
    $("#unlock-group-form-modal").scrollTop(0)

    this.dispatcher.dispatchPreparedAction(
      groupUnlockScheduleDemoAction({
        form: "district-admin",
        group: this.args.model.group,
        groupTitle: titleFromBooking ?? "Unknown",
      })
    )
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "User::UnlockGroupForm::DistrictAdmin": typeof UnlockGroupFormDistrictAdmin
  }
}
